const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const LIBRARY_NAME = 'Family Vault';
const GRAPH = 'https://graph.microsoft.com/v1.0';

let cachedDriveId = null;

export async function resolveDriveId(token) {
  if (cachedDriveId) return cachedDriveId;
  const siteRes = await fetch(`${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!siteRes.ok) throw new Error('Failed to resolve site');
  const site = await siteRes.json();
  const drivesRes = await fetch(`${GRAPH}/sites/${site.id}/drives?$select=name,id&$top=100`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!drivesRes.ok) throw new Error('Failed to load drives');
  const drivesData = await drivesRes.json();
  const drive = (drivesData.value || []).find(d => d.name.toLowerCase() === LIBRARY_NAME.toLowerCase());
  if (!drive) throw new Error('Family Vault not found');
  cachedDriveId = drive.id;
  return drive.id;
}

function getFileExtension(name) {
  return (name || '').split('.').pop().toLowerCase();
}

function getMediaType(ext) {
  const map = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg', jpeg: 'image/jpeg',
    png: 'image/png', gif: 'image/gif', webp: 'image/webp',
    svg: 'image/svg+xml',
  };
  return map[ext] || 'application/octet-stream';
}

export async function fetchVaultDocForJarvis(token, vaultDocId, vaultDocPath) {
  const driveId = await resolveDriveId(token);

  // Get the file item to find the download URL
  let fileId = vaultDocId;
  let fileName = '';

  if (fileId) {
    const itemRes = await fetch(`${GRAPH}/drives/${driveId}/items/${fileId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (itemRes.ok) {
      const item = await itemRes.json();
      fileName = item.name || '';
    }
  }

  if (!fileName && vaultDocPath) {
    // Extract filename from URL path
    try {
      const url = new URL(vaultDocPath);
      fileName = decodeURIComponent(url.pathname.split('/').pop());
    } catch {
      fileName = vaultDocPath.split('/').pop();
    }
  }

  // Download the file content
  const contentRes = await fetch(`${GRAPH}/drives/${driveId}/items/${fileId}/content`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!contentRes.ok) throw new Error(`Failed to download document (${contentRes.status})`);
  const blob = await contentRes.blob();

  const ext = getFileExtension(fileName);
  const mediaType = getMediaType(ext);

  // Convert to base64
  const base64 = await blobToBase64(blob);

  const isPdf = ext === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);

  if (isPdf) {
    return {
      type: 'document',
      source: { type: 'base64', media_type: 'application/pdf', data: base64 },
      _fileName: fileName,
    };
  }

  if (isImage) {
    return {
      type: 'image',
      source: { type: 'base64', media_type: mediaType, data: base64 },
      _fileName: fileName,
    };
  }

  // Office documents — convert to PDF via Graph API, then send to Anthropic
  const officeExts = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
  if (officeExts.includes(ext)) {
    try {
      const pdfRes = await fetch(`${GRAPH}/drives/${driveId}/items/${fileId}/content?format=pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (pdfRes.ok) {
        const pdfBlob = await pdfRes.blob();
        const pdfBase64 = await blobToBase64(pdfBlob);
        return {
          type: 'document',
          source: { type: 'base64', media_type: 'application/pdf', data: pdfBase64 },
          _fileName: fileName,
        };
      }
    } catch { /* fall through to text extraction */ }
  }

  // Plain text files (.txt, .csv, .json, etc.)
  try {
    const text = await blob.text();
    if (text && !text.includes('\0') && text.length < 50000) {
      return { type: 'text', text: `Document: ${fileName}\n\n${text}`, fileName };
    }
  } catch { /* ignore */ }

  // Final fallback
  return {
    type: 'text',
    text: `Document: ${fileName}\n(This file type cannot be directly read. The user should have it open alongside for reference.)`,
    fileName,
  };
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // Strip the data:xxx;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
